import { Box, Divider, Paper, Typography } from '@mui/material';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DashboardStats } from '../../../interfaces/Dashboard/DashboardStats';
import { formatCurrency } from '../../../utils/Currency';
import TrendyolLogo from '../../../assets/trendyol.jpeg';
import HepsiburadaLogo from '../../../assets/hepsiburada.svg';
import BoynerLogo from '../../../assets/boyner.svg';
import N11Logo from '../../../assets/n11.png';
import PazaramaLogo from '../../../assets/pazarama.png';
import WebLogo from '../../../assets/web.png';
import { sharedColors } from '../../../utils/Style';

type RevenueLine = {
  name: string;
  logo: string;
  amount: number;
  to: string;
};

type RevenueRowProps = {
  line: RevenueLine;
};

const RevenueRow = ({ line }: RevenueRowProps) => {
  return (
    <Box
      component={Link}
      to={line.to}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        textDecoration: 'none',
      }}
    >
      <Box
        component='img'
        src={line.logo}
        alt={line.name}
        width={24}
        height={24}
        sx={{ ml: '6px' }}
      />

      <Typography variant='h6' sx={{ color: sharedColors.gray5 }}>
        {formatCurrency(line.amount)}
      </Typography>
    </Box>
  );
};

type TotalRevenueCardProps = {
  stats: DashboardStats;
};

export const TotalRevenueCard = ({ stats }: TotalRevenueCardProps) => {
  const lines: RevenueLine[] = useMemo(
    () =>
      [
        {
          name: 'Trendyol',
          logo: TrendyolLogo,
          amount: stats.totalTrendyolRevenue,
          to: '/sales/trendyol',
        },
        {
          name: 'Hepsiburada',
          logo: HepsiburadaLogo,
          amount: stats.totalHepsiburadaRevenue,
          to: '/sales/hepsiburada',
        },
        {
          name: 'Boyner',
          logo: BoynerLogo,
          amount: stats.totalBoynerRevenue,
          to: '/sales/boyner',
        },
        {
          name: 'N11',
          logo: N11Logo,
          amount: stats.totalN11Revenue,
          to: '/sales/n11',
        },
        {
          name: 'Pazarama',
          logo: PazaramaLogo,
          amount: stats.totalPazaramaRevenue,
          to: '/sales/pazarama',
        },
        {
          name: 'Web Sitesi',
          logo: WebLogo,
          amount: stats.totalRevenue,
          to: '/sales/orders',
        },
      ].sort((a, b) => b.amount - a.amount),
    [stats],
  );

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        py: 3,
        px: 2,
        gap: 3,
        boxSizing: 'border-box',
        height: '100%',
      }}
    >
      <Typography variant='h6'>Toplam Gelir</Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <CurrencyExchangeIcon fontSize='large' sx={{ color: '#9499d8' }} />

          <Box sx={{ flexGrow: 1 }} />

          <Typography variant='h5'>
            {formatCurrency(
              stats.totalRevenue +
                stats.totalTrendyolRevenue +
                stats.totalHepsiburadaRevenue +
                stats.totalBoynerRevenue,
            )}
          </Typography>
        </Box>

        <Divider />

        {lines.map((line) => (
          <RevenueRow key={line.name} line={line} />
        ))}
      </Box>
    </Paper>
  );
};
